import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';

// Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if Supabase is configured
const isSupabaseConfigured = !!(supabaseUrl && supabaseAnonKey);

if (!isSupabaseConfigured) {
  console.warn(
    '⚠️ Supabase not configured. Please create a .env file with:\n' +
    'VITE_SUPABASE_URL=https://atheiieeufpwqhrkjtix.supabase.co\n' +
    'VITE_SUPABASE_ANON_KEY=your_anon_key_here\n\n' +
    'Get your keys from: https://supabase.com/dashboard/project/atheiieeufpwqhrkjtix/settings/api'
  );
}

// ---------------------------------------------------------------------------
// CHIPS cookie helpers: Partitioned cookies persist in third-party iframes
// even when the iframe is destroyed and recreated (user navigates away on the
// parent site and comes back).  We store ONLY the two tokens, not the full
// Supabase session blob, keeping each cookie well under the 4 KB limit.
//
// NOTE: Do NOT use the __Host- prefix. It conflicts with SameSite=None which
// is required for CHIPS.
// ---------------------------------------------------------------------------
const CHIPS_AT = 'enhance_sb_at';
const CHIPS_RT = 'enhance_sb_rt';

function setChipsCookie(name: string, value: string, maxAgeSec = 60 * 60 * 24 * 7) {
  const cookieStr = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; Secure; SameSite=None; Partitioned`;
  document.cookie = cookieStr;
  console.log(`[CHIPS] SET ${name} (${value.length} chars), cookie jar length=${document.cookie.length}`);
}

function getChipsCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function clearChipsCookie(name: string) {
  document.cookie = `${name}=; path=/; max-age=0; Secure; SameSite=None; Partitioned`;
}

export function saveSessionToChips(accessToken: string, refreshToken: string) {
  try {
    setChipsCookie(CHIPS_AT, accessToken);
    setChipsCookie(CHIPS_RT, refreshToken);
    const verify = getChipsCookie(CHIPS_AT);
    console.log(`[CHIPS] Verify after save: AT=${verify ? 'readable' : 'NOT readable'}`);
  } catch (e) {
    console.warn('[CHIPS] Failed to save cookies:', e);
  }
}

export function readSessionFromChips(): { access_token: string; refresh_token: string } | null {
  const at = getChipsCookie(CHIPS_AT);
  const rt = getChipsCookie(CHIPS_RT);
  console.log(`[CHIPS] Read: AT=${at ? `found(${at.length}ch)` : 'null'}, RT=${rt ? `found(${rt.length}ch)` : 'null'}`);
  if (at && rt) return { access_token: at, refresh_token: rt };
  return null;
}

export function clearChipsSession() {
  clearChipsCookie(CHIPS_AT);
  clearChipsCookie(CHIPS_RT);
  console.log('[CHIPS] Cleared');
}

// Diagnostics: runs on module load
if (typeof window !== 'undefined') {
  const inFrame = window.parent !== window;
  const sbKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
  const chips = readSessionFromChips();
  console.warn(`[DIAG] v2 | inIframe=${inFrame}, localStorage sbKey=${sbKey ? 'FOUND' : 'MISSING'}, chipsTokens=${chips ? 'FOUND' : 'MISSING'}, referrer=${document.referrer}, allCookies=${document.cookie.length}chars`);
}

// Create Supabase client
export const supabase = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    global: {
      headers: {
        'x-application-name': 'enhance-companies',
      },
    },
  }
);

// Sessionless client for public forms (apply, meet-us).
// Never attaches a JWT, so inserts always run as the anon role
// even when the visitor has a stale or expired auth session.
export const supabaseAnon = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
    global: {
      headers: {
        'x-application-name': 'enhance-companies',
      },
    },
  }
);

// Export configuration status
export { isSupabaseConfigured };

// Fetch every row of a query, paging past PostgREST's 1000-row response cap.
// Pass a builder factory (not a builder) because each page needs a fresh query.
export async function fetchAllRows<T>(
  buildQuery: () => { range: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }> },
  pageSize = 1000
): Promise<{ data: T[]; error: { message: string } | null }> {
  const all: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) return { data: all, error };
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < pageSize) break;
  }
  return { data: all, error: null };
}

// Helper function to check if user is authenticated
export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Helper function to sign out
export const signOut = async () => {
  clearChipsSession();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  if (window.parent !== window) {
    let parentOrigin = 'https://closio.com';
    try {
      const ref = document.referrer;
      if (ref) {
        const url = new URL(ref);
        if (url.origin === 'https://closio.com' || url.origin.endsWith('.closio.com')) parentOrigin = url.origin;
      }
    } catch { /* ignore */ }
    window.parent.postMessage({ type: 'ENHANCE_LOGOUT' }, parentOrigin);
  }
};

// Profile picture upload helper
export const uploadProfilePicture = async (userId: string, file: File) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/avatar.${fileExt}`;
  const filePath = fileName;

  // Upload to storage bucket
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, { 
      upsert: true,
      contentType: file.type 
    });

  if (uploadError) {
    throw uploadError;
  }

  // Get public URL
  const { data } = supabase.storage
    .from('avatars')
    .getPublicUrl(filePath);

  return data.publicUrl;
};

// Get profile picture URL
export const getProfilePictureUrl = (userId: string, fileName?: string) => {
  if (!fileName) return null;
  const { data } = supabase.storage
    .from('avatars')
    .getPublicUrl(`${userId}/${fileName}`);
  return data.publicUrl;
};
